import { reactive } from 'vue';
import { supabase } from '../supabase.js';
import { coreState } from './core.js';
import { coordinateUtils } from '../utils/coordinateUtils.js';
import { useAuth } from './useAuth.js';

function encodeSession(session) {
    if (!session) return null;
    try {
        const json = JSON.stringify(session);
        return btoa(unescape(encodeURIComponent(json)));
    } catch (e) { return null; }
}

function decodeSession(str) {
    if (!str) return null;
    try {
        const json = decodeURIComponent(escape(atob(str)));
        return JSON.parse(json);
    } catch (e) { return null; }
}

export const useSitesStore = () => {

    const auth = useAuth(); // [NEW] Access Auth Store

    async function fetchSites(shopId) {
        if (!shopId) {
            console.warn("[DataDebug] fetchSites called with null shopId");
            return [];
        }

        const OFFLINE_KEY = `__lwt_sites_${shopId}`;

        // [STALE-WHILE-REVALIDATE FIX] ALWAYS restore sites from cache synchronously first
        try {
            const cachedSitesStr = localStorage.getItem(OFFLINE_KEY);
            if (cachedSitesStr) {
                const parsedSites = decodeSession(cachedSitesStr);
                if (parsedSites && Array.isArray(parsedSites)) {
                    coreState.diveSites = parsedSites;
                    console.log(`[SitesStore] Restored ${parsedSites.length} dive sites from offline cache for shop ${shopId}.`);
                }
            }
        } catch (e) {
            console.warn("[SitesStore] Failed to parse offline sites cache.", e);
        }

        if (!navigator.onLine) {
             console.log(`[SitesStore] Offline. Skipping network fetchSites for shop ${shopId}, relying on cache.`);
             return coreState.diveSites;
        }

        // [FIX] Ensure Session Validity - bail out if session is invalid


        const { data, error } = await supabase
            .from('sites')
            .select('*')
            .eq('shop_id', shopId);


        if (error) {
            console.error("[SitesStore] Fetch Sites Error:", error);
            // [FIX] If this is an auth error (expired JWT), don't overwrite
            // the cache we restored above with an empty array.
            const isAuthError = error.code === 'PGRST301' || error.message?.includes('JWT') ||
                                error.code === '401' || error.message?.includes('invalid_grant');
            if (isAuthError) {
                console.warn("[SitesStore] Auth error detected. Preserving cached sites.");
                return coreState.diveSites;
            }
            return coreState.diveSites.length > 0 ? coreState.diveSites : [];
        }

        console.log(`[DataDebug] fetchSites success. Rows returned: ${data?.length || 0}`);

        // [POISON GUARD] An empty result with no error often means RLS filtered
        // every row (auth degraded silently — JWT dead but the request completed).
        // Refuse to overwrite a non-empty cache with empty data; the legitimate
        // "shop has no sites yet" case self-corrects on the next successful fetch.
        if (!data || data.length === 0) {
            try {
                const existingStr = localStorage.getItem(OFFLINE_KEY);
                if (existingStr) {
                    const existing = decodeSession(existingStr);
                    if (Array.isArray(existing) && existing.length > 0) {
                        console.warn(`[SitesStore] Refusing empty-result write for shop ${shopId} — cache has ${existing.length} sites, suspected auth degradation.`);
                        coreState.diveSites = existing;
                        return existing;
                    }
                }
            } catch (e) { }
        }

        // Map to Model
        coreState.diveSites = data.map(s => ({
            id: s.id,
            shopId: s.shop_id,
            name: s.name,
            coordinates: (typeof s.lat === 'number' && typeof s.lng === 'number')
                ? [s.lng, s.lat]
                : coordinateUtils.parseCoordinates(s.coordinates || s.location),
            description: s.description,
            location: '',
            depth: s.depth_range,
            min_depth: s.min_depth,
            max_depth: s.max_depth,
            depth_unit: s.depth_unit || 'm',
            difficulty: Array.isArray(s.difficulty) ? s.difficulty : [],
            structure: Array.isArray(s.structure) ? s.structure : [],
            sailTime: s.sail_time || '',
            entryType: Array.isArray(s.entry_type) ? s.entry_type : [],
            status: s.status,
            isDraft: s.status === 'draft',
            media: {
                images: s.photos || [],
                video: s.hero_video || ''
            },
            tags: [],
            fishkinds: s.fishkinds || []
        }));

        // [OFFLINE CACHE] Dump sites back to localStorage
        try {
             const OFFLINE_KEY = `__lwt_sites_${shopId}`;
             localStorage.setItem(OFFLINE_KEY, encodeSession(coreState.diveSites));
        } catch (e) {
             console.warn("[SitesStore] Failed to write offline sites to cache", e);
        }

        return coreState.diveSites;
    }

    async function createSite(siteData) {
        if (!navigator.onLine) throw new Error("You must be online to create a site.");
        let locationWKT = null;
        let lat = null;
        let lng = null;

        if (siteData.coordinates && siteData.coordinates.length === 2) {
            lng = siteData.coordinates[0];
            lat = siteData.coordinates[1];
            if (!isNaN(lng) && !isNaN(lat)) {
                locationWKT = `POINT(${lng} ${lat})`;
            }
        }

        const media = siteData.media || { images: [], video: '' };
        const payload = {
            shop_id: siteData.shopId,
            name: siteData.name,
            description: siteData.description,
            location: locationWKT,
            lat: lat,
            lng: lng,
            min_depth: siteData.min_depth,
            max_depth: siteData.max_depth,
            depth_unit: siteData.depth_unit,
            difficulty: siteData.difficulty ?? siteData.attributes?.difficulty ?? [],
            structure: siteData.structure ?? siteData.attributes?.structure ?? [],
            sail_time: siteData.sailTime ?? siteData.attributes?.sailTime ?? '',
            entry_type: siteData.entryType ?? siteData.attributes?.entryType ?? [],
            hero_video: media.video || '',
            photos: media.images || [],
            status: siteData.status || 'draft',
            fishkinds: siteData.fishkinds || siteData.attributes?.fishkinds || []
        };

        const { data, error } = await supabase
            .from('sites')
            .insert(payload)
            .select()
            .single();

        if (error) throw error;

        const newSite = {
            id: data.id,
            shopId: data.shop_id,
            name: data.name,
            location: data.description,
            coordinates: [data.lng, data.lat],
            description: data.description,
            depth: data.depth_range,
            min_depth: data.min_depth,
            max_depth: data.max_depth,
            depth_unit: data.depth_unit,
            difficulty: data.difficulty,
            structure: data.structure,
            sailTime: data.sail_time,
            entryType: data.entry_type,
            media: {
                video: data.hero_video,
                images: data.photos
            },
            status: data.status,
            isDraft: data.status === 'draft',
            tags: [],
            fishkinds: data.fishkinds || []
        };

        coreState.diveSites.push(newSite);
        return newSite;
    }

    async function updateSite(siteId, updates) {
        if (!navigator.onLine) throw new Error("You must be online to update a site.");
        const payload = {};
        const directKeys = ['name', 'description', 'status', 'shop_id', 'hero_video', 'fishkinds'];
        const keyMap = {
            'min_depth': 'min_depth',
            'max_depth': 'max_depth',
            'depth_unit': 'depth_unit',
            'sailTime': 'sail_time',
            'entryType': 'entry_type'
        };

        Object.keys(updates).forEach(key => {
            if (directKeys.includes(key)) {
                payload[key] = updates[key];
            } else if (keyMap[key]) {
                payload[keyMap[key]] = updates[key];
            } else if (['difficulty', 'structure'].includes(key)) {
                payload[key] = updates[key];
            } else if (key === 'media') {
                if (updates.media) {
                    payload.hero_video = updates.media.video || '';
                    payload.photos = updates.media.images || [];
                }
            } else if (key === 'coordinates') {
                if (updates.coordinates && updates.coordinates.length === 2) {
                    const l = updates.coordinates[0]; const t = updates.coordinates[1];
                    if (!isNaN(l) && !isNaN(t)) {
                        payload.location = `POINT(${l} ${t})`;
                        payload.lat = t;
                        payload.lng = l;
                    }
                }
            }
        });

        const { data, error } = await supabase
            .from('sites')
            .update(payload)
            .eq('id', siteId)
            .select()
            .single();

        if (error) {
            console.error("[SitesStore] DB Update Error:", error);
            throw error;
        }

        const index = coreState.diveSites.findIndex(s => s.id === siteId);
        if (index !== -1) {
            coreState.diveSites[index] = {
                ...coreState.diveSites[index],
                ...updates,
                status: data.status,
                isDraft: data.status === 'draft'
            };
        }
        return coreState.diveSites[index];
    }

    async function deleteSite(siteId) {
        if (!navigator.onLine) throw new Error("You must be online to delete a site.");
        // Soft delete for B2B integrity
        const { error } = await supabase
            .from('sites')
            .update({ status: 'archived' })
            .eq('id', siteId);

        if (error) throw error;

        const index = coreState.diveSites.findIndex(s => s.id === siteId);
        if (index !== -1) {
            // Keep in local state but mark archived? 
            // Usually we want to remove from active UI list.
            coreState.diveSites.splice(index, 1);
        }
    }

    async function generateDescription(promptType, draftText, contextData) {
        if (!navigator.onLine) throw new Error("You must be online to generate descriptions.");
        try {
            const { data, error } = await supabase.functions.invoke('generate-description', {
                body: {
                    promptType,
                    draft: draftText,
                    contextData
                }
            });

            if (error) throw error;

            return data.generatedText;

        } catch (error) {
            console.error("[SitesStore] Generate Description Error:", error);
            throw error;
        }
    }

    return {
        fetchSites,
        createSite,
        updateSite,
        deleteSite,
        generateDescription
    };
};
